import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarDialog } from "@/components/CalendarDialog";
import {
  Globe,
  MapPin,
  Star,
  Megaphone,
  Cpu,
  Mail,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const services = [
  {
    icon: Globe,
    title: "SMART Websites",
    description:
      "Fast, mobile-first websites designed to convert visitors into leads. Subscription-based, always up-to-date, and built specifically for trade businesses.",
    href: "/services/websites",
    highlights: ["Mobile-first design", "Lead forms & click-to-call", "SEO-optimised"],
  },
  {
    icon: MapPin,
    title: "Google Business Profile & SEO",
    description:
      "Get found first in your target suburbs. We optimise your Google Business Profile, build local SEO dominance, and drive organic traffic that converts.",
    href: "/services/gbp-seo",
    highlights: ["Google Maps ranking", "Local citation building", "Monthly reporting"],
  },
  {
    icon: Star,
    title: "Reputation Management & Reviews",
    description:
      "Automated 5-star review collection, QR codes, follow-ups, and reputation monitoring that builds trust and credibility on autopilot.",
    href: "/services/reputation",
    highlights: ["Automated review requests", "Negative review protection", "Social proof"],
  },
  {
    icon: Megaphone,
    title: "Lead Generation Ads",
    description:
      "Targeted Google & Facebook ad campaigns with pre-qualified leads and instant follow-up. Quality leads ready to book — not tyre-kickers.",
    href: "/services/lead-generation",
    highlights: ["Google & Facebook Ads", "Pre-qualified leads", "ROI tracking"],
  },
  {
    icon: Cpu,
    title: "Automation & AI Tools",
    description:
      "Missed-call text-back, CRM automations, AI chatbots, and smart follow-ups that respond instantly so you never miss a job.",
    href: "/services/automation",
    highlights: ["Missed-call text-back", "AI chatbot", "CRM automations"],
  },
  {
    icon: Mail,
    title: "Reactivation Campaigns",
    description:
      "Smart email & SMS campaigns to past customers. High-ROI campaigns that turn your existing database into new revenue.",
    href: "/services/reactivation",
    highlights: ["SMS & email campaigns", "Database segmentation", "ROI tracking"],
  },
];

const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  provider: {
    "@type": "Organization",
    name: "FullScale Marketing",
    url: "https://fullscalemarketing.com.au",
  },
  serviceType: "Digital Marketing for Trade Businesses",
  areaServed: { "@type": "Country", name: "Australia" },
  description: "Complete digital marketing services for Australian tradies: websites, SEO, ads, reviews, automation, and reactivation campaigns.",
};

export default function Services() {
  return (
    <>
      <Helmet>
        <title>Digital Marketing Services for Tradies | FullScale Marketing Australia</title>
        <meta
          name="description"
          content="Explore our full range of digital marketing services for tradies: SMART websites, local SEO, Google reviews, lead generation ads, automation, and reactivation campaigns."
        />
        <link rel="canonical" href="https://fullscalemarketing.com.au/services" />
        <meta property="og:title" content="Digital Marketing Services for Tradies | FullScale Marketing" />
        <meta property="og:description" content="Complete digital marketing for Australian trade businesses. Websites, SEO, ads, reviews, and automation." />
        <meta property="og:url" content="https://fullscalemarketing.com.au/services" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-14 sm:pb-18 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(100_25%_97%)] to-[hsl(98_30%_95%/0.6)]" />
        <div className="absolute inset-0 bg-gradient-hero-glow pointer-events-none" />
        <div className="absolute inset-0 grid-pattern opacity-30" />

        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="section-badge mb-4">
              <span className="text-xs">Our Services</span>
            </div>
            <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-[3.25rem] font-extrabold text-foreground leading-[1.1] mb-4 tracking-[-0.025em]">
              Digital Marketing Services for{" "}
              <span className="text-primary">Trade Businesses</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-[1.7] max-w-2xl mx-auto mb-6">
              A complete, modular growth system — pick what you need now and scale as your business grows. Every service is designed specifically for how trade businesses win work.
            </p>
            <CalendarDialog
              buttonVariant="default"
              buttonSize="lg"
              buttonClassName="h-12 text-sm font-bold rounded-xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
              >
                <Link
                  to={service.href}
                  className="group block bg-card rounded-xl p-6 sm:p-7 border border-border/60 shadow-card hover:shadow-xl hover:border-primary/30 transition-all duration-300 h-full"
                >
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <service.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {service.title}
                  </h2>
                  <p className="text-muted-foreground text-[0.9375rem] leading-[1.7] mb-4">
                    {service.description}
                  </p>

                  {/* Highlights */}
                  <ul className="space-y-1.5 mb-4">
                    {service.highlights.map((highlight, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground text-xs">{highlight}</span>
                      </li>
                    ))}
                  </ul>

                  <span className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold group-hover:gap-2.5 transition-all">
                    Learn more
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works mini */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-8"
          >
            <h2 className="heading-section text-foreground mb-3">
              Not Sure Where to <span className="text-gradient">Start?</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto leading-[1.7]">
              Book a free strategy call and we'll audit your current digital presence, then recommend the right services for your trade business — no obligation, no pressure.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8">
            {[
              { step: "01", title: "Free Strategy Call", text: "We audit your digital presence and understand your goals." },
              { step: "02", title: "Custom Plan", text: "We recommend the right services and provide clear pricing." },
              { step: "03", title: "Launch & Grow", text: "We build, launch, and optimise — you focus on the tools." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card rounded-xl p-5 border border-border/50 shadow-card text-center relative"
              >
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-sm font-bold">
                  {item.step}
                </div>
                <h3 className="text-base font-bold text-foreground mb-1.5">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-[1.6]">{item.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <CalendarDialog
              buttonVariant="default"
              buttonSize="lg"
              buttonClassName="h-12 text-sm font-bold rounded-xl"
            />
          </div>
        </div>
      </section>
    </>
  );
}
